/**
 * Mouse/pen drag-scroll + wheel for the category chip rail (touch keeps native pan).
 * Bind whenever the rail can overflow — not only when count > threshold — so phone
 * frames and short viewports stay draggable. Skip drag when `editable` so chip
 * edit targets stay clickable; wheel still pans.
 */
export function bindMenuFlowChipRail(
  rail: HTMLElement,
  options?: { editable?: boolean },
): () => void {
  const editable = Boolean(options?.editable);
  const DRAG_THRESHOLD_PX = 4;
  let dragging = false;
  let moved = false;
  let pointerId: number | null = null;
  let startX = 0;
  let startScroll = 0;
  let suppressClick = false;

  const canScroll = () => rail.scrollWidth > rail.clientWidth + 2;

  const endDrag = () => {
    if (!dragging) {
      return;
    }
    dragging = false;
    rail.classList.remove('ws-menu-flow__rail--dragging');
    if (pointerId != null && rail.hasPointerCapture(pointerId)) {
      try {
        rail.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
    }
    pointerId = null;
    if (moved) {
      suppressClick = true;
      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
    }
    moved = false;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (editable || event.pointerType === 'touch' || event.button !== 0) {
      return;
    }
    if (!canScroll()) {
      return;
    }
    dragging = true;
    moved = false;
    pointerId = event.pointerId;
    startX = event.clientX;
    startScroll = rail.scrollLeft;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!dragging || event.pointerId !== pointerId) {
      return;
    }
    const dx = event.clientX - startX;
    if (!moved && Math.abs(dx) < DRAG_THRESHOLD_PX) {
      return;
    }
    if (!moved) {
      moved = true;
      rail.classList.add('ws-menu-flow__rail--dragging');
      try {
        rail.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      window.getSelection()?.removeAllRanges();
    }
    event.preventDefault();
    rail.scrollLeft = startScroll - dx;
  };

  const onClickCapture = (event: MouseEvent) => {
    if (!suppressClick) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const onWheel = (event: WheelEvent) => {
    if (!canScroll()) {
      return;
    }
    const dominantX =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (dominantX === 0) {
      return;
    }
    const prev = rail.scrollLeft;
    rail.scrollLeft += dominantX;
    if (rail.scrollLeft !== prev) {
      event.preventDefault();
    }
  };

  rail.addEventListener('pointerdown', onPointerDown);
  rail.addEventListener('pointermove', onPointerMove);
  rail.addEventListener('pointerup', endDrag);
  rail.addEventListener('pointercancel', endDrag);
  rail.addEventListener('click', onClickCapture, true);
  rail.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    endDrag();
    rail.removeEventListener('pointerdown', onPointerDown);
    rail.removeEventListener('pointermove', onPointerMove);
    rail.removeEventListener('pointerup', endDrag);
    rail.removeEventListener('pointercancel', endDrag);
    rail.removeEventListener('click', onClickCapture, true);
    rail.removeEventListener('wheel', onWheel);
  };
}
